import { isFunction } from 'underscore';

const DATA_GUARD = 'data-focus-guard';
const TABBABLES: string[] = [
  'button:not([disabled]):not([readonly])',
  'select:not([disabled]):not([readonly])',
  'textarea:not([disabled]):not([readonly])',
  'input:not([disabled]):not([readonly])',
  `[tabindex]:not([${DATA_GUARD}]):not([tabindex="-1"]):not([disabled])`,
  'a[href]:not([disabled])',
  'area[href]:not([disabled])',
];

export const QUERY_TABBABLES: string = TABBABLES.join(',');

export const toArray = <T>(a: ArrayLike<T>): T[] => {
  const ret = Array<T>(a.length);
  for (let i = 0; i < a.length; i += 1) {
    ret[i] = a[i];
  }
  return ret;
};

export const getFrontGuards = (parent: HTMLElement | null, focusId: string): HTMLElement | null => (parent ? parent.querySelector(`#guard-front-${focusId}[${DATA_GUARD}]`) : null);

export const getTabbableNodes = (parent: HTMLElement | null): HTMLElement[] | null => (parent ? toArray(parent.querySelectorAll(QUERY_TABBABLES)) : null);

export const addDataFocus = (parent: HTMLElement | null): void => parent && !parent.hasAttribute('data-focused') && parent.setAttribute('data-focused', '');

export const removeDataFocus = (parent: HTMLElement | null): void => parent && parent.hasAttribute('data-focused') && parent.removeAttribute('data-focused');

let targetToFocusOnNext: HTMLElement | undefined;
let callbackOfFocusOnNext: (() => void) | undefined;

const getWindow = (rootElement: HTMLElement | null): Window => (
  rootElement && rootElement.ownerDocument && rootElement.ownerDocument.defaultView
    ? rootElement.ownerDocument.defaultView
    : window);

// use fabric focus async
// https://github.com/microsoft/fluentui/blob/4c8fcc01e046eb90ef6ee26310c072d38acf137f/packages/utilities/src/focus.ts
export const focusAsync = (element?: HTMLElement | null, callback?: () => void, isReturnFocus: boolean = false): void => {
  if (element) {
    // An element was already queued to be focused, so replace that one with the new element
    if (targetToFocusOnNext && isReturnFocus) {
      // normally a return focus should happen after the targetToFocusOnNext is focused
      // in this case targetToFocusOnNext should be null and won't go inside this function
      // this is to resolve the situation that one element to focus is override by the return focus
      // from another focus lock editor
      return;
    } if (targetToFocusOnNext) {
      targetToFocusOnNext = element;
      // callback need to be updated on every new focusAsync
      callbackOfFocusOnNext = callback;

      return;
    }

    callbackOfFocusOnNext = callback;
    targetToFocusOnNext = element;

    const win = getWindow(element);

    if (win) {
      win.requestAnimationFrame(() => {
        if (targetToFocusOnNext && isFunction(targetToFocusOnNext.focus)) {
          targetToFocusOnNext.focus({
            preventScroll: true,
          });
          if (isFunction(callbackOfFocusOnNext)) {
            callbackOfFocusOnNext();
          }

          // We are done focusing for this frame, so reset the queued focus element
          targetToFocusOnNext = undefined;
        }
      });
    }
  }
};
